import { useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useGovernanceStore } from './useGovernanceStore';
import { useApiToken } from '@/auth/hooks';
import { toast } from 'sonner';

// Default tools (v1) — only shown if not yet in DB
const DEFAULT_TOOLS = [
  { toolId: 'marketplace', toolLabel: 'Marketplace' },
  { toolId: 'workspace', toolLabel: 'Workspace' },
  { toolId: 'connector-hub', toolLabel: 'Connector Hub' },
];

export function PoliciesTab() {
  const { getAccessToken } = useApiToken();
  const { policies, policiesLoading, loadPolicies, updatePolicy } = useGovernanceStore();

  useEffect(() => {
    void loadPolicies(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge policies from DB with default tools
  const mergedPolicies = DEFAULT_TOOLS.map((dt) => {
    const existing = policies.find((p) => p.toolId === dt.toolId);
    return {
      toolId: dt.toolId,
      toolLabel: existing?.toolLabel ?? dt.toolLabel,
      requiresApproval: existing?.requiresApproval ?? false,
      approverRole: existing?.approverRole ?? 'Admin',
      updatedAt: existing?.updatedAt ?? null,
    };
  });

  // Debounced update
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleUpdate = useCallback((toolId: string, toolLabel: string, requiresApproval: boolean, approverRole: string) => {
    if (debounceTimers.current[toolId]) clearTimeout(debounceTimers.current[toolId]);
    debounceTimers.current[toolId] = setTimeout(async () => {
      try {
        await updatePolicy(toolId, { toolLabel, requiresApproval, approverRole }, getAccessToken);
        toast.success('Richtlinie aktualisiert');
      } catch {
        toast.error('Fehler beim Aktualisieren der Richtlinie');
      }
    }, 500);
  }, [updatePolicy, getAccessToken]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Genehmigungs-Richtlinien</h3>
        <p className="text-sm text-muted-foreground">
          Legen Sie fest, welche Aktionen eine Genehmigung erfordern.
        </p>
      </div>

      {policiesLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!policiesLoading && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tool · Genehmigung · Genehmiger · Zuletzt geändert
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {mergedPolicies.map((policy) => (
              <div key={policy.toolId} className="flex items-center gap-4 border-b pb-3 last:border-b-0 last:pb-0">
                <span className="min-w-[140px] text-sm font-medium">{policy.toolLabel}</span>
                <Switch
                  checked={policy.requiresApproval}
                  onCheckedChange={(checked) =>
                    handleUpdate(policy.toolId, policy.toolLabel, checked, policy.approverRole)
                  }
                />
                <Select
                  value={policy.approverRole}
                  onValueChange={(value) =>
                    handleUpdate(policy.toolId, policy.toolLabel, policy.requiresApproval, value)
                  }
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="SuperAdmin">SuperAdmin</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">
                  {policy.updatedAt
                    ? new Date(policy.updatedAt).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })
                    : '—'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
